import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Download, Share } from "lucide-react";
import { getInstallEvent, onInstallEvent, promptInstall } from "../lib/installPrompt";

// Three PWA "profiles". Each area of the site can be installed as its own
// home-screen icon, with its own manifest, name, colour and start URL.
const PROFILES = {
  pulizie: {
    manifest: "/manifest-pulizie.webmanifest",
    icon: "/icons/icon-pulizie-192.png",
    themeColor: "#4a1c17",
    title: "Pulizie",
  },
  admin: {
    manifest: "/manifest-admin.webmanifest",
    icon: "/icons/icon-admin-192.png",
    themeColor: "#000000",
    title: "Admin",
  },
  checkin: {
    manifest: "/manifest-checkin.webmanifest",
    icon: "/icons/icon-checkin-192.png",
    themeColor: "#4a1c17",
    title: "Check-in",
  },
  ristorante: {
    manifest: "/manifest-ristorante.webmanifest",
    icon: "/icons/icon-ristorante-192.png",
    themeColor: "#4a1c17",
    title: "Ristorante",
  },
  prenotazioni: {
    manifest: "/manifest-prenotazioni.webmanifest",
    icon: "/icons/icon-prenotazioni-192.png",
    themeColor: "#4a1c17",
    title: "Prenotazioni",
  },
  ambienti: {
    manifest: "/manifest-ambienti.webmanifest",
    icon: "/icons/icon-ambienti-192.png",
    themeColor: "#2b7a46",
    title: "Ambienti",
  },
  menu: {
    manifest: "/manifest-menu.webmanifest",
    icon: "/icons/icon-menu-192.png",
    themeColor: "#4a1c17",
    title: "Menu",
  },
};

function detectProfile(pathname) {
  // Standalone housekeeping route (public, no /admin prefix)
  if (pathname.startsWith("/pulizie")) return "pulizie";
  // Housekeeping inside admin still uses the same profile
  if (pathname.startsWith("/admin/pulizie")) return "pulizie";
  if (pathname.startsWith("/checkin")) return "checkin";
  if (pathname.startsWith("/ristorante")) return "ristorante";
  if (pathname.startsWith("/prenotazioni")) return "prenotazioni";
  if (pathname.startsWith("/ambienti")) return "ambienti";
  if (pathname.startsWith("/admin")) return "admin";
  return "menu";
}

function setLinkHref(id, href) {
  const el = document.getElementById(id);
  if (el) el.setAttribute("href", href);
}
function setMetaContent(id, content) {
  const el = document.getElementById(id);
  if (el) el.setAttribute("content", content);
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_TTL_DAYS = 14;

export default function PWAManager() {
  const location = useLocation();
  const [installEvent, setInstallEvent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [profile, setProfile] = useState(detectProfile(location.pathname));

  // Switch manifest + icons + theme + title whenever route changes area.
  useEffect(() => {
    const key = detectProfile(location.pathname);
    setProfile(key);
    const p = PROFILES[key];
    setLinkHref("app-manifest", p.manifest);
    setLinkHref("apple-icon", p.icon);
    setMetaContent("app-theme-color", p.themeColor);
    setMetaContent("apple-web-app-title", p.title);
  }, [location.pathname]);

  // Capture the browser's beforeinstallprompt event (Android / Chrome).
  useEffect(() => {
    setInstallEvent(getInstallEvent());
    return onInstallEvent(setInstallEvent);
  }, []);

  // Decide when to show the banner (menu + pulizie + admin, once every 14 days per profile).
  useEffect(() => {
    if (isStandalone()) {
      setShowBanner(false);
      return;
    }
    const raw = localStorage.getItem(DISMISS_KEY + "_" + profile);
    if (raw) {
      const dismissedAt = Number(raw);
      const days = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (days < DISMISS_TTL_DAYS) {
        setShowBanner(false);
        return;
      }
    }
    if (installEvent || isIOS()) {
      const t = setTimeout(() => setShowBanner(true), 3500);
      return () => clearTimeout(t);
    }
  }, [profile, installEvent]);

  const install = async () => {
    if (installEvent) {
      await promptInstall();
      setShowBanner(false);
      localStorage.setItem(DISMISS_KEY + "_" + profile, String(Date.now()));
    }
  };
  const dismiss = () => {
    setShowBanner(false);
    localStorage.setItem(DISMISS_KEY + "_" + profile, String(Date.now()));
  };

  if (!showBanner) return null;

  const ios = isIOS();
  const bannerConfig = profile === "pulizie"
    ? {
        icon: "/icons/icon-pulizie-192.png",
        title: "Aggiungi Pulizie alla Home",
        subtitle: "Apri la lista lavori con un tocco, come un'app.",
      }
    : profile === "admin"
    ? {
        icon: "/icons/icon-admin-192.png",
        title: "Aggiungi Admin alla Home",
        subtitle: "Gestione rapida di prenotazioni, clienti e menu.",
      }
    : profile === "prenotazioni"
    ? {
        icon: "/icons/icon-prenotazioni-192.png",
        title: "Aggiungi Prenotazioni alla Home",
        subtitle: "Spa, massaggi, eventi e cavallo con un tocco, come un'app.",
      }
    : profile === "ristorante"
    ? {
        icon: "/icons/icon-ristorante-192.png",
        title: "Aggiungi Ristorante alla Home",
        subtitle: "Sala, tavoli e prenotazioni con un tocco.",
      }
    : profile === "checkin"
    ? {
        icon: "/icons/icon-checkin-192.png",
        title: "Aggiungi Check-in alla Home",
        subtitle: "Arrivi, partenze e codici con un tocco.",
      }
    : {
        icon: "/icons/icon-menu-192.png",
        title: "Aggiungi Il Pozzo alla Home",
        subtitle: "Apri il menu con un tocco, come una vera app — senza scaricare nulla.",
      };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-3 pointer-events-none"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-md mx-auto rounded-2xl bg-[#4a1c17] text-[#f7ebdf] shadow-2xl border border-[#f7ebdf]/10 pointer-events-auto overflow-hidden">
        <div className="flex items-start gap-3 px-4 py-3">
          <img
            src={bannerConfig.icon}
            alt=""
            className="h-12 w-12 rounded-xl shrink-0 bg-white"
          />
          <div className="flex-1 min-w-0">
            <div className="font-serif text-base font-bold leading-tight">
              {bannerConfig.title}
            </div>
            <div className="text-xs text-[#f7ebdf]/80 mt-0.5 leading-snug">
              {bannerConfig.subtitle}
            </div>
          </div>
          <button
            onClick={dismiss}
            className="p-1 rounded-full hover:bg-[#f7ebdf]/10 shrink-0"
            aria-label="Chiudi"
            data-testid="pwa-install-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {ios ? (
          <div className="px-4 pb-3 text-xs text-[#f7ebdf]/85 leading-relaxed border-t border-[#f7ebdf]/10 pt-2 flex items-center gap-2">
            <Share className="h-4 w-4 shrink-0" />
            <span>
              Tocca <b>Condividi</b> e poi <b>Aggiungi alla schermata Home</b>.
            </span>
          </div>
        ) : (
          <div className="px-4 pb-3 flex justify-end border-t border-[#f7ebdf]/10 pt-2">
            <button
              onClick={install}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#f7ebdf] text-[#4a1c17] px-4 py-1.5 text-sm font-semibold"
              data-testid="pwa-install-btn"
            >
              <Download className="h-4 w-4" /> Installa
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
